import { useState } from 'react';

import { Icon } from '~/modules/elements/akui/icon';
import events from '~/modules/game-events/game-events';
import { useEventListener } from '~/modules/game-events/hooks';
import RemoteMicClient from '~/modules/remote-mic/network/client';
import useFullscreenToggle from '~/modules/toolbar/use-fullscreen-toggle';
import AddPhoneSheet from '~/routes/remote-mic/components/add-phone-sheet';
import RenameSheet from '~/routes/remote-mic/components/rename-sheet';
import useRemoteMicName from '~/routes/remote-mic/hooks/use-remote-mic-name';
import Ping from '~/routes/remote-mic/panels/microphone/ping';
import { ConnectionStatuses } from '~/routes/remote-mic/remote-mic';

const statusDotByStatus: Record<ConnectionStatuses, string> = {
  uninitialised: 'bg-gray-400',
  connecting: 'bg-blue-400',
  reconnecting: 'bg-amber-400',
  connected: 'bg-green-400',
  disconnected: 'bg-red-400',
  error: 'bg-red-400',
};

const statusLabelByStatus: Record<ConnectionStatuses, string> = {
  uninitialised: 'Not connected',
  connecting: 'Connecting',
  reconnecting: 'Reconnecting',
  connected: 'Live',
  disconnected: 'Disconnected',
  error: 'Error',
};

interface Props {
  connectionStatus: ConnectionStatuses;
  roomId: string | null;
  isMicOn: boolean;
  // Muted by the person holding the phone; the television not listening on a menu is not muted.
  micMuted: boolean;
  // The singer's name stays off the bar until the phone has finished joining.
  joined: boolean;
}

// The bar is the one place that says the microphone is live, now that the level meter is gone.
function TopBar({ connectionStatus, roomId, isMicOn, micMuted, joined }: Props) {
  const [isAddPhoneOpen, setIsAddPhoneOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const { name, setName } = useRemoteMicName();
  const [playerNumber] = useEventListener(events.remoteMicPlayerSet, true) ?? [null];
  const fullscreen = useFullscreenToggle({ remote: true });

  const isConnected = connectionStatus === 'connected';
  const micOff = isConnected && micMuted;

  return (
    <>
      <AddPhoneSheet open={isAddPhoneOpen} onClose={() => setIsAddPhoneOpen(false)} roomId={roomId} />
      <RenameSheet
        open={isRenameOpen}
        currentName={name}
        onClose={() => setIsRenameOpen(false)}
        onSave={(newName) => {
          setName(newName);
          RemoteMicClient.renameSelf(newName);
        }}
      />
      <div
        data-test="phone-top-bar"
        data-player-number={playerNumber ?? 'none'}
        data-is-mic-on={isConnected && isMicOn}
        className="grid h-9.5 shrink-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 bg-black px-5 text-white">
        <span
          className="flex items-center gap-[0.4375rem] text-[0.75rem] font-bold tracking-[0.06em] text-white/75 uppercase"
          data-test="connection-status"
          data-status={connectionStatus}>
          <span
            aria-hidden
            className={`size-2 shrink-0 rounded-full ${micOff ? 'bg-red-400' : statusDotByStatus[connectionStatus]}`}
          />
          <span>{micOff ? 'Mic off' : statusLabelByStatus[connectionStatus]}</span>
          {isConnected && !micMuted && (
            <span className="text-white/55">
              <Ping />
            </span>
          )}
        </span>
        <span className="flex min-w-0 justify-center">
          {isConnected && joined && (
            <button
              type="button"
              className="ph-no-capture flex min-w-0 cursor-pointer items-center gap-1.5 border-none bg-transparent text-[0.8125rem] font-bold text-white/85"
              onClick={() => setIsRenameOpen(true)}
              data-test="topbar-player-name">
              <span className="truncate">{name}</span>
              <Icon icon="ic:baseline-edit" size={3.5} className="shrink-0 opacity-60" />
            </button>
          )}
        </span>
        <span className="flex items-center gap-3.5 text-[0.75rem] font-bold tracking-[0.1em] text-white/55">
          <span data-test="topbar-room">{roomId ? roomId.toUpperCase() : '—'}</span>
          <button
            type="button"
            onClick={() => setIsAddPhoneOpen(true)}
            aria-label="Add another phone"
            data-test="open-qr"
            className="flex cursor-pointer items-center border-none bg-transparent text-inherit">
            <Icon icon="ic:baseline-qr-code-2" size={4.5} />
          </button>
          {fullscreen.supported && (
            <button
              type="button"
              onClick={fullscreen.toggle}
              aria-label="Toggle fullscreen"
              data-test="toggle-fullscreen"
              className="flex cursor-pointer items-center border-none bg-transparent text-inherit">
              <Icon
                icon={fullscreen.isFullScreen ? 'ic:baseline-fullscreen-exit' : 'ic:baseline-fullscreen'}
                size={4.5}
              />
            </button>
          )}
        </span>
      </div>
    </>
  );
}

export default TopBar;
